import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Truck,
  FileText,
  DollarSign,
  UserCircle,
  ChevronRight,
  Share2,
  Check,
  BarChart2,
  BookOpen,
} from "lucide-react";
import { Button, Badge, Skeleton } from "@musa.tech/design-system";
import { toast } from "sonner";
import { usePoliticasContent, PoliticaContent, PoliticaContentType } from "@/hooks/usePoliticasContent";
import { openCloudHumansChat } from "@/utils/cloudHumans";
import { useAnalytics } from "@/hooks/useAnalytics";
import { generateSlug, buildArticleShareUrl } from "@/utils/slugUtils";

type CategoriaConfig = {
  label: string;
  descricao: string;
  icon: React.ElementType;
  tipo: PoliticaContentType;
};

const CATEGORIAS_CONFIG: Record<string, CategoriaConfig> = {
  coletas: {
    label: "Coletas",
    descricao: "Coletas recorrentes, sob demanda e status.",
    icon: Truck,
    tipo: "Coletas",
  },
  documentos: {
    label: "Documentos",
    descricao: "MTR, evidências e comprovantes.",
    icon: FileText,
    tipo: "Documentação",
  },
  financeiro: {
    label: "Financeiro",
    descricao: "Consulta de faturas e previsão de cobrança.",
    icon: DollarSign,
    tipo: "Financeiro",
  },
  "conta-e-acesso": {
    label: "Conta e acesso",
    descricao: "Login, usuários e permissões.",
    icon: UserCircle,
    tipo: "Usuários e permissões",
  },
  dados: {
    label: "Dados",
    descricao: "Dashboard, métricas de impacto e relatórios.",
    icon: BarChart2,
    tipo: "Dados",
  },
  geral: {
    label: "Geral",
    descricao: "Informações gerais sobre o Portal do Gerador.",
    icon: BookOpen,
    tipo: "Geral",
  },
};

const FAQ_BADGE_CLASSES =
  "bg-muted/60 text-muted-foreground border-0 text-[10px] px-2 py-0.5 font-medium uppercase tracking-wide";

const CentralDeAjudaCategoria = () => {
  const { categoria } = useParams<{ categoria: string }>();
  const navigate = useNavigate();
  const { politicas, isLoading } = usePoliticasContent();
  const { trackButtonClick } = useAnalytics();
  const [selecionada, setSelecionada] = useState<PoliticaContent | null>(null);
  const [copied, setCopied] = useState(false);
  const [searchParams] = useSearchParams();
  const artigoSlug = searchParams.get('artigo');
  const q = searchParams.get('q');

  const config = categoria ? CATEGORIAS_CONFIG[categoria] : null;

  const itens = useMemo(() => {
    if (!politicas) return [];
    if (q) {
      const termo = q.toLowerCase();
      return politicas.filter((p) => p.is_visible && p.title.toLowerCase().includes(termo));
    }
    if (!config) return [];
    return politicas.filter((p) => p.type === config.tipo && p.is_visible);
  }, [config, politicas, q]);

  // Primeiro artigo aberto por padrão
  const artigoAtivo = selecionada ?? itens[0] ?? null;

  useEffect(() => {
    if (artigoSlug && itens.length > 0 && !selecionada) {
      const found = itens.find(p => generateSlug(p.title) === artigoSlug);
      if (found) setSelecionada(found);
    }
  }, [artigoSlug, itens]);

  const shareUrl = artigoAtivo
    ? buildArticleShareUrl(artigoAtivo.title, artigoAtivo.type)
    : "";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copiado para a área de transferência");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Erro ao copiar link");
    }
  };

  if (!config) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-6 flex flex-col items-center justify-center">
        <p className="text-neutral-500 text-sm">Categoria não encontrada.</p>
        <Button
          variant="ghost"
          className="mt-4 gap-2"
          onClick={() => navigate("/ajuda")}
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar à Central de Ajuda
        </Button>
      </div>
    );
  }

  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-background w-full overflow-x-hidden">
      {/* Header */}
      <div className="border-b border-neutral-200 bg-white px-4 md:px-6 py-4">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-neutral-500 mb-3">
            <button
              type="button"
              onClick={() => navigate("/ajuda")}
              className="hover:text-primary-600 transition-colors"
            >
              Central de Ajuda
            </button>
            <ChevronRight className="h-3 w-3" />
            <span className="text-neutral-800 font-medium">{config.label}</span>
          </nav>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/ajuda")}
              className="h-8 w-8 text-neutral-500 hover:text-neutral-800"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary-100">
                <Icon className="h-4 w-4 text-primary-600" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-neutral-900">
                  {config.label}
                </h1>
                <p className="text-xs text-neutral-500">{config.descricao}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Layout master-detail */}
      <div className="max-w-6xl mx-auto flex gap-0 md:gap-6 p-4 md:p-6">
        {/* Sidebar */}
        <aside className="hidden md:block w-56 flex-shrink-0">
          <div className="sticky top-6 space-y-1">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider px-3 mb-2">
              {q ? `Resultados para "${q}"` : config.label}
            </p>
            {isLoading ? (
              <div className="space-y-1.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full rounded-md" />
                ))}
              </div>
            ) : itens.length === 0 ? (
              <p className="text-xs text-neutral-400 px-3">Nenhum artigo ainda.</p>
            ) : (
              itens.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid="central-ajuda-item-artigo"
                  onClick={() => {
                    // Só cliques deliberados contam. O primeiro artigo da lista
                    // abre sozinho ao entrar na tela (`artigoAtivo` faz fallback
                    // para `itens[0]`) e não gera registro, para não inflar a
                    // métrica em 1 por acesso (GR-712, Suposição 02).
                    trackButtonClick({
                      buttonLabel: "central_ajuda_visualizar_artigo",
                      categoria: config.label,
                      titulo_artigo: p.title,
                    });
                    setSelecionada(p);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-xs transition-colors ${
                    artigoAtivo?.id === p.id
                      ? "bg-primary-100 text-primary-700 font-semibold"
                      : "text-neutral-700 hover:bg-neutral-100"
                  }`}
                >
                  {p.title}
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Conteúdo do artigo */}
        <main className="flex-1 min-w-0">
          {isLoading ? (
            <div className="space-y-4 bg-white rounded-lg border border-neutral-200 p-8">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-7 w-3/4" />
              <div className="space-y-2 pt-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </div>
          ) : itens.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon className="h-10 w-10 text-neutral-300 mb-3" />
              <p className="text-sm font-medium text-neutral-700">
                {q ? `Nenhum resultado para "${q}"` : "Nenhum artigo disponível ainda"}
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                {q ? "Tente outros termos de busca." : `Em breve teremos conteúdo sobre ${config.label.toLowerCase()}.`}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-4 gap-2 text-primary-600"
                onClick={() => navigate("/ajuda")}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Voltar à Central de Ajuda
              </Button>
            </div>
          ) : artigoAtivo ? (
            <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
              {/* Cabeçalho do artigo */}
              <header className="px-8 pt-8 pb-6 border-b border-border/40">
                <div className="space-y-3">
                  {artigoAtivo.type && (
                    <Badge className={FAQ_BADGE_CLASSES}>
                      {artigoAtivo.type}
                    </Badge>
                  )}
                  <h2 className="text-2xl font-semibold leading-tight text-foreground tracking-tight">
                    {artigoAtivo.title}
                  </h2>
                </div>
              </header>

              {/* Conteúdo */}
              <article className="px-8 py-6">
                <div
                  className="faq-content"
                  dangerouslySetInnerHTML={{ __html: artigoAtivo.content.html }}
                />
              </article>

              {/* Rodapé */}
              <footer className="flex justify-between items-center px-8 py-4 border-t border-border/40 bg-muted/30">
                <p className="text-xs text-muted-foreground">
                  Ficou com dúvidas?{" "}
                  <button
                    type="button"
                    onClick={openCloudHumansChat}
                    className="text-primary hover:underline font-medium"
                  >
                    Entre em contato com nosso suporte
                  </button>
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyLink}
                  className="gap-2 text-muted-foreground hover:text-foreground"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 text-green-500" />
                      <span className="text-green-600">Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Share2 className="h-4 w-4" />
                      Compartilhar
                    </>
                  )}
                </Button>
              </footer>
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default CentralDeAjudaCategoria;
